import { Head, Link, router } from "@inertiajs/react"
import React from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Log {
  id: number
  log_type: string
  log_date: string | null
  title: string | null
  content_preview: string | null
  user_name: string | null
  assigned_to_name: string | null
  status: number
  urgent: boolean
  sensitive: boolean
  significant: boolean
  completed: boolean
  completed_on: string | null
  due_date: string | null
  can_edit: boolean
  can_delete: boolean
  request: string | null
  completed_by_name: string | null
  results_notes: string | null
  activities: {
    notes: string | null
    activity_date: string | null
    user_name: string | null
  }[]
}

interface Props {
  case_record: CaseRecord
  logs: Log[]
  current_filter: string
  can_view_restricted: boolean
  can_view_accounting: boolean
}

const typeBadge = (type: string) => {
  const styles: Record<string, string> = {
    update: "bg-hpi-light-blue text-hpi-dark-blue",
    todo: "bg-hpi-yellow/30 text-hpi-dark-gray",
    restricted: "bg-hpi-red/20 text-hpi-red",
    accounting: "bg-hpi-dark-green/20 text-hpi-dark-green",
  }
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs ${styles[type] || "bg-hpi-pale-gray text-hpi-dark-gray"}`}
    >
      {type}
    </span>
  )
}

export default function Index({
  case_record,
  logs,
  current_filter,
  can_view_restricted,
  can_view_accounting,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Logs", href: `/cases/${case_record.id}/logs` },
  ]

  const basePath = `/cases/${case_record.id}/logs`

  const filters = [
    { key: "all", label: "All" },
    { key: "update", label: "Updates" },
    { key: "todo", label: "Todos" },
    ...(can_view_restricted
      ? [{ key: "restricted", label: "Restricted" }]
      : []),
    ...(can_view_accounting
      ? [{ key: "accounting", label: "Accounting" }]
      : []),
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this log?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  const handleComplete = (id: number) => {
    router.visit(`${basePath}/${id}`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Logs - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Logs for {case_record.display_name}
          </h1>
          <div className="flex gap-2">
            <Link
              href={`/cases/${case_record.id}/time_entries/new`}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              New Time Entry
            </Link>
            <Link
              href={`/cases/${case_record.id}/expenses/new`}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              New Expense
            </Link>
            <Link
              href={`${basePath}/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Log
            </Link>
          </div>
        </div>

        <div className="mb-4 flex gap-2">
          {filters.map((f) => (
            <Link
              key={f.key}
              href={f.key === "all" ? basePath : `${basePath}?type=${f.key}`}
              className={`rounded px-3 py-1 text-sm ${
                current_filter === f.key
                  ? "bg-hpi-dark-blue text-white"
                  : "bg-hpi-pale-gray text-hpi-dark-gray hover:bg-hpi-pale-gray"
              }`}
            >
              {f.label}
            </Link>
          ))}
        </div>

        {logs.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Content
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Author
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const hasTodoRollup =
                  log.log_type === "todo" &&
                  (log.request ||
                    log.activities.length > 0 ||
                    (log.completed && log.results_notes))

                return (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-hpi-pale-blue">
                      <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                        {formatDate(log.log_date)}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        {typeBadge(log.log_type)}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        <Link
                          href={`${basePath}/${log.id}`}
                          className="text-hpi-active-blue hover:text-hpi-active-blue"
                        >
                          {log.title || log.content_preview || "(No content)"}
                        </Link>
                        {log.activities.length > 0 && (
                          <span className="text-hpi-med-gray ml-2 text-xs">
                            ({log.activities.length}{" "}
                            {log.activities.length === 1 ? "note" : "notes"})
                          </span>
                        )}
                        {log.urgent && (
                          <span className="bg-hpi-red/20 text-hpi-red ml-2 rounded px-2 py-0.5 text-xs">
                            Urgent
                          </span>
                        )}
                        {log.sensitive && (
                          <span className="ml-2 rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
                            Sensitive
                          </span>
                        )}
                        {log.significant && (
                          <span className="ml-2 rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                            Significant
                          </span>
                        )}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        {log.user_name}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        {log.completed ? (
                          <span className="bg-hpi-dark-green/20 text-hpi-dark-green rounded px-2 py-0.5 text-xs">
                            Complete
                          </span>
                        ) : log.log_type === "todo" && log.due_date ? (
                          <span className="bg-hpi-yellow/30 text-hpi-dark-gray rounded px-2 py-0.5 text-xs">
                            Due {formatDate(log.due_date)}
                          </span>
                        ) : (
                          <span className="bg-hpi-pale-gray text-hpi-dark-gray rounded px-2 py-0.5 text-xs">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                        <Link
                          href={`${basePath}/${log.id}`}
                          className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                        >
                          View
                        </Link>
                        {log.can_edit && (
                          <Link
                            href={`${basePath}/${log.id}/edit`}
                            className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                          >
                            Edit
                          </Link>
                        )}
                        {log.log_type === "todo" && !log.completed && (
                          <button
                            onClick={() => handleComplete(log.id)}
                            className="text-hpi-dark-green hover:text-hpi-dark-green mr-2"
                          >
                            Complete
                          </button>
                        )}
                        {log.can_delete && (
                          <button
                            onClick={() => handleDelete(log.id)}
                            className="text-hpi-red hover:text-hpi-red"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    {hasTodoRollup && (
                      <tr>
                        <td
                          colSpan={6}
                          className="border-hpi-light-gray border px-4 py-0"
                        >
                          <div className="border-hpi-light-blue bg-hpi-pale-blue/40 my-1 rounded border-l-4 px-4 py-2 text-sm">
                            {log.request && (
                              <div className="mb-1">
                                <span className="text-hpi-dark-gray font-semibold">
                                  Request:{" "}
                                </span>
                                <span className="text-hpi-dark-gray">
                                  {log.request}
                                </span>
                              </div>
                            )}
                            {log.activities.length > 0 && (
                              <div
                                className={
                                  log.request
                                    ? "border-hpi-light-gray mt-1 border-t pt-1"
                                    : ""
                                }
                              >
                                <div className="text-hpi-med-gray mb-1 text-xs font-medium uppercase">
                                  Activity ({log.activities.length}{" "}
                                  {log.activities.length === 1
                                    ? "note"
                                    : "notes"}
                                  )
                                </div>
                                <div className="space-y-0.5">
                                  {log.activities.map((activity, idx) => (
                                    <div
                                      key={idx}
                                      className="border-hpi-light-gray text-hpi-dark-gray flex border-l-2 pl-2 text-xs"
                                    >
                                      <span className="text-hpi-med-gray mr-1 whitespace-nowrap">
                                        {formatDate(activity.activity_date)}
                                      </span>
                                      {activity.user_name && (
                                        <span className="mr-1 font-medium">
                                          - {activity.user_name}:
                                        </span>
                                      )}
                                      <span>{activity.notes}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {log.completed && log.results_notes && (
                              <div className="border-hpi-light-gray text-hpi-dark-green mt-1 border-t pt-1 text-xs">
                                <span className="font-medium">
                                  Completed
                                  {log.completed_by_name
                                    ? ` by ${log.completed_by_name}`
                                    : ""}
                                  {log.completed_on
                                    ? ` on ${formatDate(log.completed_on)}`
                                    : ""}
                                  :
                                </span>{" "}
                                {log.results_notes}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No logs found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
